(function initPmaSavedProducts(global) {
  const doc = global.document;
  if (!doc) return;

  const FEEDBACK_HIDE_MS = 3200;

  function readProxyBase(root) {
    const raw = root?.getAttribute('data-pma-saved-products-proxy') || '/apps/pma-saved-products';
    return raw.replace(/\/$/, '');
  }

  function updateCountBadges(count) {
    doc.querySelectorAll('[data-pma-saved-products-count]').forEach((node) => {
      node.textContent = String(count);
      node.hidden = count <= 0;
    });
  }

  function findFeedback(button) {
    return button?.closest('.pma-product-card__save-slot, .pma-product-card__saved-entry, .pma-buybox__saved-entry')?.querySelector('[data-pma-saved-feedback]');
  }

  function setFeedback(button, { message = '', visible = false, isError = false } = {}) {
    const note = findFeedback(button);
    if (!note) return;
    if (!visible) {
      note.hidden = true;
      note.textContent = '';
      note.classList.remove('is-error');
      return;
    }
    note.hidden = false;
    note.textContent = message;
    note.classList.toggle('is-error', isError);
  }

  function scheduleFeedbackHide(button, { hideWhenSaved = false } = {}) {
    const note = findFeedback(button);
    if (!note) return;
    const hideToken = String(Date.now());
    note.dataset.pmaFeedbackHideToken = hideToken;
    global.setTimeout(() => {
      if (note.dataset.pmaFeedbackHideToken !== hideToken) return;
      if (!hideWhenSaved && button.getAttribute('aria-pressed') === 'true') return;
      setFeedback(button, { visible: false });
    }, FEEDBACK_HIDE_MS);
  }

  function setToggleState(button, saved) {
    if (!button) return;
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    const saveLabel = button.getAttribute('data-label-save') || 'Save';
    const removeLabel = button.getAttribute('data-label-remove') || 'Remove from saved';
    const productTitle = button.getAttribute('data-product-title') || '';
    const ariaLabel = saved
      ? (productTitle ? `${removeLabel}: ${productTitle}` : removeLabel)
      : (productTitle ? `${saveLabel}: ${productTitle}` : saveLabel);
    button.setAttribute('aria-label', ariaLabel);
    button.classList.toggle('is-saved', saved);
    const hiddenLabel = button.querySelector('[data-pma-saved-toggle-label]');
    if (hiddenLabel) {
      hiddenLabel.textContent = saved ? removeLabel : saveLabel;
    } else {
      button.textContent = saved ? removeLabel : saveLabel;
    }
    button.removeAttribute('data-pma-saved-error');
  }

  function syncTogglesForHandle(handle, saved, count) {
    const selector = `[data-pma-saved-toggle][data-product-handle="${CSS.escape(handle)}"]`;
    doc.querySelectorAll(selector).forEach((button) => {
      setToggleState(button, saved);
      const note = findFeedback(button);
      if (!note) return;
      if (saved) {
        const compactCatalog =
          Boolean(button.closest('.pma-product-card--compact-catalog'));

        setFeedback(button, {
          message: note.getAttribute('data-note-saved') || 'Saved to your account.',
          visible: compactCatalog === false,
        });
      } else {
        setFeedback(button, { visible: false });
      }
    });
    updateCountBadges(count);
  }

  async function postToggle(proxyBase, handle) {
    const response = await fetch(`${proxyBase}/toggle`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
        accept: 'application/json',
      },
      body: JSON.stringify({ handle }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || !payload.ok) {
      const message = payload.error || 'Saved products request failed.';
      throw new Error(message);
    }
    return payload;
  }

  function bindToggleButtons(proxyBase) {
    doc.querySelectorAll('[data-pma-saved-toggle]').forEach((button) => {
      if (button.dataset.pmaSavedToggleBound === 'true') return;
      button.dataset.pmaSavedToggleBound = 'true';
      const handle = button.getAttribute('data-product-handle');
      if (!handle) return;

      button.addEventListener('click', async () => {
        button.disabled = true;
        const note = findFeedback(button);
        try {
          const result = await postToggle(proxyBase, handle);
          const saved = Boolean(result.saved);
          syncTogglesForHandle(handle, saved, Number(result.count || 0));
          try {
            const trackFn =
              (global.PMAAnalytics && typeof global.PMAAnalytics.track === 'function' && global.PMAAnalytics.track.bind(global.PMAAnalytics)) ||
              (global.PMA && global.PMA.analytics && typeof global.PMA.analytics.track === 'function' && global.PMA.analytics.track.bind(global.PMA.analytics));
            if (trackFn) {
              const active = Boolean(doc.body && doc.body.classList.contains('is-member-active'));
              trackFn(
                saved ? 'pma_order_plan_add' : 'pma_order_plan_remove',
                {
                  surface: 'saved_products',
                  item_id: String(handle).slice(0, 100),
                  cart_state: active ? 'active' : 'locked',
                },
                { consent_class: 'analytics' },
              );
            }
          } catch (_) {
            /* analytics must never block save UX */
          }
          if (note) {
            if (saved) {
              setFeedback(button, {
                message: note.getAttribute('data-note-saved') || 'Saved to your account.',
                visible: true,
              });

              if (button.closest('.pma-product-card--compact-catalog')) {
                scheduleFeedbackHide(button, { hideWhenSaved: true });
              }
            } else {
              setFeedback(button, {
                message: note.getAttribute('data-note-removed') || 'Removed from saved.',
                visible: true,
              });
              scheduleFeedbackHide(button);
            }
          }
          const listItem = button.closest('[data-pma-saved-handle]');
          if (!saved && listItem) {
            listItem.remove();
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'error';
          button.setAttribute('data-pma-saved-error', message);
          setFeedback(button, {
            message: note?.getAttribute('data-note-error') || message,
            visible: true,
            isError: true,
          });
        } finally {
          button.disabled = false;
        }
      });
    });
  }

  function initRoots() {
    doc.querySelectorAll('[data-pma-saved-products-mode="persistence"]').forEach((root) => {
      const proxyBase = readProxyBase(root);
      bindToggleButtons(proxyBase);
    });
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', initRoots);
  } else {
    initRoots();
  }
})(typeof window !== 'undefined' ? window : globalThis);
